import omniApi from '@/api/omni';
import type { Module } from 'vuex';
import type { OmniAccount, OmniConversation, OmniMessage, QuickReplyItem } from '@/types/omni';

export interface OmniState {
  shops: OmniAccount[];
  shopsLoading: boolean;
  conversations: OmniConversation[];
  conversationsTotal: number;
  conversationsLoading: boolean;
  selectedPlatform: string;
  selectedShopId: string;
  onlyUnread: boolean;
  searchKeyword: string;
  activeConversationId: string;
  activeConversation: OmniConversation | null;
  messages: OmniMessage[];
  messagesTotal: number;
  messagesPage: number;
  messagesTotalPages: number;
  messagesLoading: boolean;
  totalUnread: number;
  shopUnreadMap: Record<string, number>;
  platformCounts: {
    all: number;
    pdd: number;
    xianyu: number;
  };
  quickReplies: QuickReplyItem[];
  quickRepliesLoading: boolean;
}

function getConvTimestamp(c: any): number {
  if (!c) return 0;
  const val = c.lastMessage?.sendTime || c.updatedAt || c.createdAt || 0;
  if (typeof val === 'number') {
    return val < 1e11 && val > 0 ? val * 1000 : val;
  }
  if (typeof val === 'string') {
    const num = Number(val);
    if (!isNaN(num) && num > 0) {
      return num < 1e11 ? num * 1000 : num;
    }
    const parsed = new Date(val).getTime();
    if (!isNaN(parsed) && parsed > 0) return parsed;
  }
  return 0;
}

function sortConversations(list: any[]): any[] {
  if (!Array.isArray(list)) return [];
  return list.sort((a, b) => getConvTimestamp(b) - getConvTimestamp(a));
}

export function normalizeConversation(c: any): any {
  if (!c) return null;
  const conversationId = String(c.conversationId || c._id || c.chatId || '');
  const buyerUid = String(c.buyerUid || '');
  const buyerNickname =
    c.buyerNickname ||
    (buyerUid ? `买家_${buyerUid.slice(-4)}` : '买家用户');
  const buyerAvatar = c.buyerAvatar || '';
  const accountId = String(c.accountId || c.shopId || '');
  const accountName = c.accountName || c.shopName || (c.platform === 'xianyu' ? '闲鱼' : '');
  const accountLogo = c.accountLogo || c.shopLogo || '';

  let lastMessage: any = null;
  if (c.lastMessage && typeof c.lastMessage === 'object') {
    const st = Number(c.lastMessage.sendTime) || 0;
    lastMessage = {
      ...c.lastMessage,
      content: String(c.lastMessage.content || ''),
      sendTime: st < 1e11 && st > 0 ? st * 1000 : st,
      direction: c.lastMessage.direction || (c.lastMessage.fromRole === 'buyer' ? 'in' : 'out'),
      isRead: Boolean(c.lastMessage.isRead),
    };
  }

  return {
    ...c,
    conversationId,
    _id: conversationId,
    chatId: conversationId,
    buyerUid,
    buyerNickname,
    buyerAvatar,
    accountId,
    shopId: accountId,
    accountName,
    shopName: accountName,
    accountLogo,
    shopLogo: accountLogo,
    lastMessage,
    unreadCount: Number(c.unreadCount) || 0,
    hasOrder: Boolean(c.hasOrder || c.hasAutoOrder || (c.orderCount && c.orderCount > 0)),
    hasAutoOrder: Boolean(c.hasAutoOrder),
    orderCount: Number(c.orderCount) || (c.hasOrder ? 1 : 0),
    lastOrderInfo: c.lastOrderInfo || null
  };
}

export function normalizeMessage(m: any): any {
  if (!m) return null;
  const msgId = String(m.msgId || m._id || '');
  const conversationId = String(m.conversationId || m.chatId || '');
  const buyerUid = String(m.buyerUid || '');
  const buyerNickname = m.buyerNickname || '';
  const buyerAvatar = m.buyerAvatar || '';
  const accountId = String(m.accountId || m.shopId || '');
  const direction = m.direction || (m.fromRole === 'buyer' ? 'in' : 'out');
  const fromRole = m.fromRole || (direction === 'in' ? 'buyer' : 'cs');
  const sendTime =
    typeof m.sendTime === 'number'
      ? m.sendTime < 1e11
        ? m.sendTime * 1000
        : m.sendTime
      : new Date(m.sendTime).getTime() || Date.now();

  return {
    ...m,
    msgId,
    _id: msgId,
    conversationId,
    chatId: conversationId,
    buyerUid,
    buyerNickname,
    buyerAvatar,
    accountId,
    shopId: accountId,
    direction,
    fromRole,
    sendTime
  };
}

const omniModule: Module<OmniState, any> = {
  namespaced: true,
  state: {
    shops: [],
    shopsLoading: false,
    conversations: [],
    conversationsTotal: 0,
    conversationsLoading: false,
    selectedPlatform: 'all',
    selectedShopId: 'all',
    onlyUnread: false,
    searchKeyword: '',
    activeConversationId: '',
    activeConversation: null,
    messages: [],
    messagesTotal: 0,
    messagesPage: 1,
    messagesTotalPages: 1,
    messagesLoading: false,
    totalUnread: 0,
    shopUnreadMap: {},
    platformCounts: {
      all: 0,
      pdd: 0,
      xianyu: 0
    },
    quickReplies: [],
    quickRepliesLoading: false
  },
  getters: {
    connectedShopCount(state) {
      return (state.shops || []).filter((s) => s.wsState === 'connected').length;
    },
    reloginShopCount(state) {
      return (state.shops || []).filter((s) => s.loginState === 'relogin' || s.wsState === 'disconnected').length;
    }
  },
  mutations: {
    SET_SHOPS(state, list) {
      state.shops = Array.isArray(list) ? list : [];
    },
    SET_SHOPS_LOADING(state, val) {
      state.shopsLoading = Boolean(val);
    },
    UPSERT_SHOP(state, shop) {
      if (!shop) return;
      const shopId = shop.accountId || shop.shopId;
      if (!shopId) return;
      const idx = state.shops.findIndex((s) => s.accountId === shopId || (s as any).shopId === shopId);
      if (idx >= 0) {
        state.shops.splice(idx, 1, { ...state.shops[idx], ...shop });
      } else {
        state.shops.unshift(shop);
      }
    },
    REMOVE_SHOP(state, shopId) {
      state.shops = state.shops.filter((s) => s.accountId !== shopId && (s as any).shopId !== shopId);
    },
    SET_CONVERSATIONS(state, { list, totalCount, totalUnread, shopUnreadMap, platformCounts }: any) {
      const arr = (Array.isArray(list) ? list : []).map(normalizeConversation);
      sortConversations(arr);
      state.conversations = arr;
      if (totalCount !== undefined) state.conversationsTotal = totalCount;
      if (totalUnread !== undefined) state.totalUnread = totalUnread;
      if (shopUnreadMap !== undefined) state.shopUnreadMap = shopUnreadMap || {};
      if (platformCounts && typeof platformCounts === 'object') {
        state.platformCounts = {
          all: Number(platformCounts.all) || 0,
          pdd: Number(platformCounts.pdd) || 0,
          xianyu: Number(platformCounts.xianyu) || 0
        };
      }
      if (state.activeConversationId) {
        const updatedActive = arr.find((c) => c.conversationId === String(state.activeConversationId));
        if (updatedActive) {
          state.activeConversation = { ...state.activeConversation, ...updatedActive };
        }
      }
    },
    SET_PLATFORM_COUNTS(state, counts: any) {
      if (counts && typeof counts === 'object') {
        state.platformCounts = {
          all: Number(counts.all) || 0,
          pdd: Number(counts.pdd) || 0,
          xianyu: Number(counts.xianyu) || 0
        };
      }
    },
    SET_CONVERSATIONS_LOADING(state, val) {
      state.conversationsLoading = Boolean(val);
    },
    SET_FILTERS(state, patch: Record<string, any> = {}) {
      if (patch.selectedPlatform !== undefined) state.selectedPlatform = patch.selectedPlatform;
      if (patch.selectedShopId !== undefined) state.selectedShopId = patch.selectedShopId;
      if (patch.onlyUnread !== undefined) state.onlyUnread = patch.onlyUnread;
      if (patch.searchKeyword !== undefined) state.searchKeyword = patch.searchKeyword;
    },
    SET_ACTIVE_CONVERSATION(state, conv) {
      state.activeConversation = conv ? normalizeConversation(conv) : null;
      state.activeConversationId = state.activeConversation ? state.activeConversation.conversationId : '';
    },
    SET_MESSAGES(state, { list, totalCount, currentPage, totalPages, prepend = false }) {
      const arr = (Array.isArray(list) ? list : []).map(normalizeMessage);
      if (prepend) {
        const existingIds = new Set(state.messages.map((m) => m.msgId));
        const newItems = arr.filter((m) => !existingIds.has(m.msgId));
        state.messages = [...newItems, ...state.messages];
      } else {
        state.messages = arr;
      }
      if (totalCount !== undefined) state.messagesTotal = totalCount;
      if (currentPage !== undefined) state.messagesPage = currentPage;
      if (totalPages !== undefined) state.messagesTotalPages = totalPages;
    },
    SET_MESSAGES_LOADING(state, val) {
      state.messagesLoading = Boolean(val);
    },
    SET_QUICK_REPLIES(state, list) {
      state.quickReplies = Array.isArray(list) ? list : [];
    },
    SET_QUICK_REPLIES_LOADING(state, val) {
      state.quickRepliesLoading = Boolean(val);
    },
    CLEAR_CONVERSATION_UNREAD(state, { conversationId, shopId, count }: Record<string, any> = {}) {
      const targetId = conversationId ? String(conversationId) : '';
      let clearedCount = Number(count) || 0;

      if (targetId && Array.isArray(state.conversations)) {
        const cIdx = state.conversations.findIndex((c) => c.conversationId === targetId);
        if (cIdx >= 0) {
          const conv = state.conversations[cIdx];
          if (!clearedCount && Number(conv.unreadCount) > 0) {
            clearedCount = Number(conv.unreadCount);
          }
          state.conversations.splice(cIdx, 1, { ...conv, unreadCount: 0 });
        }
      }

      if (state.activeConversation) {
        state.totalUnread = Math.max(0, (state.totalUnread || 0) - clearedCount);
        const targetShopId = shopId || state.activeConversation?.accountId || (state.activeConversation as any)?.shopId;
        if (targetShopId && state.shopUnreadMap && state.shopUnreadMap[targetShopId]) {
          state.shopUnreadMap[targetShopId] = Math.max(0, (state.shopUnreadMap[targetShopId] || 0) - clearedCount);
        }
      }
    },
    SET_UNREAD_SUMMARY(state, { totalUnread, shopUnreadMap }) {
      if (Array.isArray(state.shops) && state.shops.length > 0 && shopUnreadMap) {
        const allowedSet = new Set(state.shops.map((s) => String(s.accountId || (s as any).shopId)));
        const scopedMap: Record<string, number> = {};
        for (const [sId, unread] of Object.entries(shopUnreadMap)) {
          if (allowedSet.has(String(sId))) {
            scopedMap[sId] = Number(unread) || 0;
          }
        }
        state.shopUnreadMap = scopedMap;
        state.totalUnread = Object.values(scopedMap).reduce((sum, n) => sum + (Number(n) || 0), 0);
      } else {
        if (totalUnread !== undefined) state.totalUnread = totalUnread;
        if (shopUnreadMap !== undefined) state.shopUnreadMap = shopUnreadMap || {};
      }
    },
    UPSERT_CONVERSATION_ON_EVENT(state, rawConversation) {
      if (!rawConversation) return;
      const conversation = normalizeConversation(rawConversation);
      const convId = conversation.conversationId;
      if (!convId) return;

      const targetShopId = conversation.accountId;
      if (Array.isArray(state.shops) && state.shops.length > 0 && targetShopId) {
        const belongsToUser = state.shops.some((s) => String(s.accountId || (s as any).shopId) === targetShopId);
        if (!belongsToUser) return;
      }

      const matchShop =
        state.selectedShopId === 'all' || !state.selectedShopId || targetShopId === String(state.selectedShopId);
      const matchPlatform =
        state.selectedPlatform === 'all' ||
        !state.selectedPlatform ||
        (conversation.platform || 'pdd') === state.selectedPlatform;

      const idx = state.conversations.findIndex((c) => c.conversationId === convId);

      const isOutbound =
        conversation.lastMessage?.direction === 'out' ||
        conversation.lastMessage?.fromRole === 'cs' ||
        conversation.lastMessage?.fromRole === 'assistant';

      let safeConv = { ...conversation };
      if (isOutbound && (safeConv.unreadCount === undefined || safeConv.unreadCount > 0)) {
        safeConv.unreadCount = 0;
      }

      if (idx >= 0) {
        const prev = state.conversations[idx];
        const merged = { ...prev, ...safeConv };
        if (isOutbound) {
          merged.unreadCount = 0;
        }
        state.conversations.splice(idx, 1, merged);
      } else {
        const plat = conversation.platform === 'xianyu' ? 'xianyu' : 'pdd';
        if (state.platformCounts) {
          state.platformCounts[plat] = (state.platformCounts[plat] || 0) + 1;
          state.platformCounts.all = (state.platformCounts.all || 0) + 1;
        }
        if (matchShop && matchPlatform) {
          state.conversations.push(safeConv);
        }
      }
      sortConversations(state.conversations);

      if (state.activeConversationId === convId) {
        const currentActive: any = state.activeConversation || {};
        state.activeConversation = { ...currentActive, ...safeConv };
      }
    },
    UPDATE_CONVERSATION_PROFILE(state, rawConversation) {
      if (!rawConversation) return;
      const norm = normalizeConversation(rawConversation);
      const convId = norm.conversationId;
      const buyerUid = norm.buyerUid;
      const accountId = norm.accountId;

      const patch: Record<string, any> = {};
      if (norm.buyerAvatar) patch.buyerAvatar = norm.buyerAvatar;
      if (norm.buyerNickname) patch.buyerNickname = norm.buyerNickname;
      if (norm.hasOrder !== undefined) patch.hasOrder = norm.hasOrder;
      if (norm.hasAutoOrder !== undefined) patch.hasAutoOrder = norm.hasAutoOrder;
      if (norm.orderCount !== undefined) patch.orderCount = norm.orderCount;
      if (norm.lastOrderInfo !== undefined) patch.lastOrderInfo = norm.lastOrderInfo;

      if (Object.keys(patch).length === 0) return;

      if (Array.isArray(state.conversations)) {
        const idx = state.conversations.findIndex((c) => {
          if (convId && c.conversationId === convId) return true;
          return c.buyerUid && buyerUid && c.buyerUid === buyerUid && (!accountId || c.accountId === accountId);
        });
        if (idx >= 0) {
          const prev = state.conversations[idx];
          state.conversations.splice(idx, 1, { ...prev, ...patch });
        }
      }

      if (state.activeConversation) {
        const curId = state.activeConversation.conversationId;
        const curBuyerUid = state.activeConversation.buyerUid;
        const isMatch = (convId && curId === convId) || (buyerUid && curBuyerUid === buyerUid);
        if (isMatch) {
          state.activeConversation = { ...state.activeConversation, ...patch };
        }
      }
    },
    MARK_CONVERSATION_ORDERED(state, { conversationId, orderCount, lastOrderInfo }: any) {
      if (!conversationId) return;
      const targetId = String(conversationId);
      if (Array.isArray(state.conversations)) {
        const idx = state.conversations.findIndex((c) => c.conversationId === targetId || (c as any)._id === targetId);
        if (idx >= 0) {
          const prev = state.conversations[idx];
          const newCount = orderCount || (prev.orderCount ? prev.orderCount + 1 : 1);
          state.conversations.splice(idx, 1, {
            ...prev,
            hasOrder: true,
            hasAutoOrder: true,
            orderCount: newCount,
            lastOrderInfo: lastOrderInfo || prev.lastOrderInfo || null,
          });
        }
      }
      if (state.activeConversation && (state.activeConversation.conversationId === targetId || (state.activeConversation as any)._id === targetId)) {
        const prev = state.activeConversation;
        const newCount = orderCount || (prev.orderCount ? prev.orderCount + 1 : 1);
        state.activeConversation = {
          ...prev,
          hasOrder: true,
          hasAutoOrder: true,
          orderCount: newCount,
          lastOrderInfo: lastOrderInfo || prev.lastOrderInfo || null,
        };
      }
    },
    APPEND_MESSAGE_ON_EVENT(state, rawMessage) {
      if (!rawMessage) return;
      const message = normalizeMessage(rawMessage);
      const targetShopId = message.accountId;
      if (Array.isArray(state.shops) && state.shops.length > 0 && targetShopId) {
        const belongsToUser = state.shops.some((s) => String(s.accountId || (s as any).shopId) === targetShopId);
        if (!belongsToUser) return;
      }

      const targetConvId = message.conversationId;
      const convBuyerUid = message.buyerUid;
      const msgContent = message.content || (message.msgType === 'image' ? '[图片]' : '');

      if (Array.isArray(state.conversations) && (targetConvId || convBuyerUid)) {
        const cIdx = state.conversations.findIndex((c) => {
          if (targetConvId && c.conversationId === targetConvId) return true;
          return (
            c.buyerUid &&
            convBuyerUid &&
            c.buyerUid === convBuyerUid &&
            (!targetShopId || c.accountId === targetShopId)
          );
        });

        if (cIdx >= 0) {
          const prevConv = state.conversations[cIdx];
          const sendTime = Number(message.sendTime) || prevConv.lastMessage?.sendTime || Date.now();
          const patch: Record<string, any> = {
            lastMessage: {
              msgId: message.msgId,
              content: msgContent,
              msgType: message.msgType || 'text',
              sendTime,
              direction: message.direction,
              fromRole: message.fromRole,
              senderUser: message.fromName,
              source: message.source,
              isRead: message.direction === 'out' ? Boolean(message.isRead) : false,
            },
          };
          if (message.direction === 'out') {
            patch.unreadCount = 0;
          }
          const mergedConv = { ...prevConv, ...patch };
          state.conversations.splice(cIdx, 1, mergedConv);
          sortConversations(state.conversations);
        }
      }

      if (targetConvId && targetConvId !== String(state.activeConversationId)) return;

      const msgKey = message.msgId;
      const existingIdx = state.messages.findIndex((m) => {
        if (msgKey && m.msgId === msgKey) return true;
        if (m.direction === 'out' && message.direction === 'out') {
          const isSameContent = m.content === message.content;
          const g1 = (m as any).rawContext?.goods?.goodsId || m.goodsInfo?.goodsId;
          const g2 = (message as any).rawContext?.goods?.goodsId || message.goodsInfo?.goodsId;
          const isSameGoods = g1 && g2 && String(g1) === String(g2);
          if (isSameContent || isSameGoods) {
            const t1 = Number(m.sendTime || 0);
            const t2 = Number(message.sendTime || 0);
            const diff = Math.abs(t1 - t2);
            if (isNaN(diff) || diff < 45000) return true;
          }
        }
        return false;
      });

      if (existingIdx >= 0) {
        const prev = state.messages[existingIdx];
        const isPrevSpecial = ['manual_workbench', 'auto_reply', 'greeting_reply', 'ai_reply', 'pdd_auto_create'].includes((prev as any).source);
        const resolvedSource = isPrevSpecial && (message as any).source === 'other_client' ? (prev as any).source : ((message as any).source || (prev as any).source);
        state.messages.splice(existingIdx, 1, {
          ...prev,
          ...message,
          msgId: prev.msgId || msgKey,
          source: resolvedSource,
          senderUser: (prev as any).senderUser || (message as any).senderUser,
          rawContext: { ...((prev as any).rawContext || {}), ...((message as any).rawContext || {}) },
        });
      } else {
        state.messages.push(message);
        state.messagesTotal = (state.messagesTotal || 0) + 1;
      }
    },
    MARK_MESSAGES_READ(state, { conversationId, buyerUid, readTime }: Record<string, any> = {}) {
      // 1. 同步更新 conversations 列表中匹配会话的最新出站消息已读标记
      const conv = (state.conversations || []).find((c) => {
        const cid = String(c.conversationId || c._id || c.chatId || '');
        return (
          (conversationId && cid === String(conversationId)) ||
          (buyerUid && String(c.buyerUid) === String(buyerUid))
        );
      });
      if (conv && conv.lastMessage?.direction === 'out') {
        conv.lastMessage.isRead = true;
      }
      if (state.activeConversation && state.activeConversation.lastMessage?.direction === 'out') {
        const activeCid = String(state.activeConversation.conversationId || state.activeConversation._id || '');
        if (
          (conversationId && activeCid === String(conversationId)) ||
          (buyerUid && String(state.activeConversation.buyerUid) === String(buyerUid))
        ) {
          state.activeConversation.lastMessage.isRead = true;
        }
      }

      // 2. 更新当前处于查看状态的消息列表中的出站消息已读状态
      const isCurrentConv =
        (conversationId && String(state.activeConversationId) === String(conversationId)) ||
        (buyerUid && state.activeConversation && String(state.activeConversation.buyerUid) === String(buyerUid));

      if (!isCurrentConv) return;

      let updated = false;
      for (let i = state.messages.length - 1; i >= 0; i--) {
        const m = state.messages[i];
        if (m.direction === 'out') {
          if (m.isRead) break;
          m.isRead = true;
          if (readTime) (m as any).readTime = readTime;
          updated = true;
        }
      }
      if (updated) {
        state.messages = [...state.messages];
      }
    },
    UPDATE_MESSAGE_ON_EVENT(state, updateInfo: Record<string, any>) {
      if (!updateInfo) return;
      const targetId = String(updateInfo.messageId || updateInfo._id || '');
      const targetMsgId = String(updateInfo.msgId || '');
      const targetConvId = String(updateInfo.conversationId || updateInfo.chatId || '');

      // 1. 同步更新当前正在查看的消息列表里的对应消息
      if (Array.isArray(state.messages) && state.messages.length > 0) {
        const idx = state.messages.findIndex((m) => {
          const mid = String(m._id || '');
          const pmid = String(m.msgId || '');
          if (targetId && (mid === targetId || pmid === targetId)) return true;
          if (targetMsgId && (pmid === targetMsgId || mid === targetMsgId)) return true;
          return false;
        });

        if (idx >= 0) {
          const prev = state.messages[idx];
          const updated = {
            ...prev,
            ...(updateInfo.surveyUrl ? { surveyUrl: updateInfo.surveyUrl } : {}),
            ...(updateInfo.displayType ? { displayType: updateInfo.displayType } : {}),
            ...(updateInfo.content ? { content: updateInfo.content } : {}),
            ...(updateInfo.isWithdrawn === true ? {
              isWithdrawn: true,
              withdrawnAt: updateInfo.withdrawnAt,
              withdrawnByMsgId: updateInfo.withdrawnByMsgId,
            } : {}),
            rawContext: {
              ...((prev as any).rawContext || {}),
              ...(updateInfo.originalImageUrl ? { originalImageUrl: updateInfo.originalImageUrl } : {}),
            },
          };
          state.messages.splice(idx, 1, updated);
        }
      }

      // 2. 同步更新会话列表里的最新摘要和 lastSurveyUrl
      if (Array.isArray(state.conversations) && targetConvId) {
        const cIdx = state.conversations.findIndex((c) => {
          const cid = String(c.conversationId || (c as any).chatId || (c as any)._id || '');
          return cid === targetConvId;
        });
        if (cIdx >= 0) {
          const prevConv = state.conversations[cIdx];
          const patch: Record<string, any> = {};
          if (updateInfo.surveyUrl) {
            patch.lastSurveyUrl = updateInfo.surveyUrl;
            patch.lastMessage = {
              ...(prevConv.lastMessage || {}),
              content: `[图片问卷] ${updateInfo.surveyUrl}`,
            };
          }
          state.conversations.splice(cIdx, 1, { ...prevConv, ...patch });
        }
      }

      // 3. 同步更新当前选中的活跃会话
      if (state.activeConversation && targetConvId) {
        const curId = String(state.activeConversation.conversationId || (state.activeConversation as any).chatId || (state.activeConversation as any)._id || '');
        if (curId === targetConvId && updateInfo.surveyUrl) {
          state.activeConversation = {
            ...state.activeConversation,
            lastSurveyUrl: updateInfo.surveyUrl,
          };
        }
      }
    }
  },
  actions: {
    async fetchShops({ commit }) {
      commit('SET_SHOPS_LOADING', true);
      try {
        const res = await omniApi.getAccounts();
        const list = res.data || [];
        commit('SET_SHOPS', list);
        return list;
      } finally {
        commit('SET_SHOPS_LOADING', false);
      }
    },

    async fetchConversations({ commit, state }, extraParams: Record<string, any> = {}) {
      commit('SET_CONVERSATIONS_LOADING', true);
      try {
        const params = {
          platform: extraParams.platform || state.selectedPlatform || 'all',
          accountId: state.selectedShopId !== 'all' ? state.selectedShopId : undefined,
          status: state.onlyUnread ? 'unread' : 'all',
          keyword: state.searchKeyword || undefined,
          page: extraParams.page || 1,
          pageSize: extraParams.pageSize || 100
        };
        const res = await omniApi.getConversations(params);
        const data: any = res.data || {};
        commit('SET_CONVERSATIONS', {
          list: data.list || [],
          totalCount: data.total || data.totalCount || 0,
          totalUnread: data.totalUnread || 0,
          shopUnreadMap: data.shopUnreadMap || {},
          platformCounts: data.platformCounts || undefined
        });
        return data;
      } catch (err) {
        return {};
      } finally {
        commit('SET_CONVERSATIONS_LOADING', false);
      }
    },

    async syncShopConversations({ commit, dispatch, state }, shopId?: string) {
      commit('SET_CONVERSATIONS_LOADING', true);
      try {
        const targetShopId = shopId || state.selectedShopId || 'all';
        const platform = state.selectedPlatform || 'all';
        const res = await omniApi.syncShopConversations(platform, targetShopId);
        await dispatch('fetchConversations');
        return res.data || {};
      } finally {
        commit('SET_CONVERSATIONS_LOADING', false);
      }
    },

    async recallMessage({ state }, { conversationId, msgId }: Record<string, any> = {}) {
      const convId = conversationId || state.activeConversationId;
      if (!convId || !msgId) return null;
      const platform = state.activeConversation?.platform || 'pdd';
      const res = await omniApi.recallMessage(platform, convId, {
        msgId,
        accountId: state.activeConversation?.accountId || (state.activeConversation as any)?.shopId
      });
      return res.data || {};
    },

    async syncConversationMessages({ commit, state }, { conversationId, size = 20, startMsgId = null }: Record<string, any> = {}) {
      const convId = conversationId || state.activeConversationId;
      if (!convId) return null;
      const platform = state.activeConversation?.platform || 'pdd';
      commit('SET_MESSAGES_LOADING', true);
      try {
        const res = await omniApi.syncConversationMessages(platform, convId, { size, startMsgId });
        const msgRes = await omniApi.getConversationMessages(platform, convId, {
          page: 1,
          pageSize: 50,
          limit: 50,
          clearUnread: true
        });
        const data: any = msgRes.data || {};
        if (data.conversation) {
          commit('SET_ACTIVE_CONVERSATION', data.conversation);
          commit('UPSERT_CONVERSATION_ON_EVENT', data.conversation);
        }
        commit('SET_MESSAGES', {
          list: data.list || [],
          totalCount: data.totalCount || 0,
          currentPage: data.currentPage || 1,
          totalPages: data.totalPages || 1,
          prepend: false
        });
        return res.data || {};
      } finally {
        commit('SET_MESSAGES_LOADING', false);
      }
    },

    async selectConversation({ commit }, conversation: any) {
      if (!conversation || !conversation.conversationId) {
        commit('SET_ACTIVE_CONVERSATION', null);
        commit('SET_MESSAGES', { list: [], totalCount: 0, currentPage: 1, totalPages: 1 });
        return;
      }

      const convId = conversation.conversationId;
      const platform = conversation.platform || 'pdd';
      const unreadCount = Number(conversation.unreadCount) || 0;
      commit('CLEAR_CONVERSATION_UNREAD', {
        conversationId: convId,
        shopId: conversation.accountId,
        count: unreadCount
      });
      commit('SET_ACTIVE_CONVERSATION', { ...conversation, unreadCount: 0 });
      commit('UPSERT_CONVERSATION_ON_EVENT', { ...conversation, unreadCount: 0 });
      commit('SET_MESSAGES_LOADING', true);

      try {
        const res = await omniApi.getConversationMessages(platform, convId, {
          page: 1,
          pageSize: 50,
          limit: 50,
          clearUnread: true
        });
        const data: any = res.data || {};
        if (data.conversation) {
          commit('SET_ACTIVE_CONVERSATION', { ...data.conversation, unreadCount: 0 });
          commit('UPSERT_CONVERSATION_ON_EVENT', { ...data.conversation, unreadCount: 0 });
        }
        commit('SET_MESSAGES', {
          list: data.list || [],
          totalCount: data.totalCount || data.total || (data.list || []).length,
          currentPage: data.currentPage || 1,
          totalPages: data.totalPages || 1,
          prepend: false
        });
        omniApi.markConversationRead(platform, convId).catch(() => {});
        return data;
      } finally {
        commit('SET_MESSAGES_LOADING', false);
      }
    },

    async loadEarlierMessages({ commit, state }) {
      if (!state.activeConversationId) return null;
      if (state.messagesLoading) return null;

      const platform = state.activeConversation?.platform || 'pdd';
      commit('SET_MESSAGES_LOADING', true);
      try {
        const earliestMsg: any = state.messages[0];
        const beforeTimestamp = earliestMsg?.sendTime || earliestMsg?.timestamp || null;
        const nextPage = (state.messagesPage || 1) + 1;

        const res = await omniApi.getConversationMessages(platform, state.activeConversationId, {
          beforeTimestamp,
          page: nextPage,
          pageSize: 50,
          limit: 30,
          clearUnread: false
        });
        const data: any = res.data || {};
        commit('SET_MESSAGES', {
          list: data.list || [],
          totalCount: data.totalCount || data.total,
          currentPage: data.currentPage || nextPage,
          totalPages: data.totalPages,
          prepend: true
        });
        return data;
      } finally {
        commit('SET_MESSAGES_LOADING', false);
      }
    },

    async fetchQuickReplies({ commit }, params: Record<string, any> = {}) {
      commit('SET_QUICK_REPLIES_LOADING', true);
      try {
        const res = await omniApi.getQuickReplies(params);
        const list = res.data || [];
        commit('SET_QUICK_REPLIES', list);
        return list;
      } finally {
        commit('SET_QUICK_REPLIES_LOADING', false);
      }
    }
  }
};

export default omniModule;
